import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Receipt from '../validation/Receipt'
import { SignedDocument } from '../../../domain/Docusign/SignedDocument';
import { ReceiptView } from '../../../domain/Receipt/ReceiptView'
import FetchFolderGateway from '../../../gateway/Folder/FetchFolderGateway'
import LocalStorageGateway from '../../../gateway/Form/LocalStorageGateway'
import GetDetailAndStoreItUseCase from '../../../useCase/Folder/GetDetailAndStoreItUseCase'
import { ErrorResponse } from '../../../domain/Api/Type'
import ErrorModal from '../modal/ErrorModal';
import SignDocument from '../validation/SignDocument';
import IconFactory from '../svg/IconFactory'
import Spinner from '../svg/Spinner';
import InvalidElement from '../../../domain/Folder/InvalidElementInterface';

interface Props {
  dossierId: string
  state: string | null
  receipts: { [key: string]: ReceiptView[] }
  documents: SignedDocument[] | null
  invalidElements: InvalidElement[]
  onInvalidateToggle: (
    isInvalid: boolean,
    elementId: string,
    comment: string,
    blockId: string,
    receiptId: string|null,
    value: string|null,
    pageId: string,
  ) => void
  showSignedDocuments?: boolean
  onCommentChange: (elementId: string, receiptId: string|null ,comment: string) => void
}

const ReceiptsValidation = ({ dossierId, state, receipts, documents, invalidElements, onInvalidateToggle, onCommentChange, showSignedDocuments=true }: Props): JSX.Element => {
  const { t } = useTranslation()
  const [isDownloading, setDownloading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const downloadAllDocuments = () => {
    if (isDownloading)
      return
    setDownloading(true)
    const getDetailAndStoreItUseCase = new GetDetailAndStoreItUseCase(new FetchFolderGateway(), new LocalStorageGateway(dossierId))

    getDetailAndStoreItUseCase?.downloadSignedDocument(dossierId)?.then((response) => {
      if (response !== null) {
        const url = URL.createObjectURL(response)
        const a = document.createElement('a')
        document.body.appendChild(a)
        a.href = url
        a.download = `dossier-${dossierId}.zip`
        a.click()
        window.URL.revokeObjectURL(url)
      }
    })
      .catch((errors: ErrorResponse) => {
        if (errors.errors?.length > 0) {
          setErrorMessage(errors.errors[0].message);
        } else {
          setErrorMessage(t(`docusign.unexpected_error`));
        }
      })
      .finally(() =>
        setDownloading(false)
      )
  }

  const getDownloadCaption = isDownloading ? t('button.download-in-progress') : t('button.download-document')
  const downloadDocWidget = (null !== documents && documents?.length > 0) ? (
    <div className="button align-items-end btn-download button-primary--outline button--width-fit button--height-fit button-enabled u-pas"
      onClick={() => downloadAllDocuments()}
    >
      <IconFactory type="download" />
      <span className={`u-prs u-pxs`}>{getDownloadCaption}</span>
    </div>
  ) : ((null !== documents) ? <Spinner /> : (<span className="u-txt-italic">{t('docusign.documents_not_available')}</span>))

  return (
    <>
      {errorMessage && <ErrorModal message={errorMessage} onClose={() => setErrorMessage(null)} />}
      <div
        className="box-elevations box-elevations--base box-elevations--mb u-mxAuto u-mbxlr">
        { showSignedDocuments &&
        <>
        <div className="flex-container u-mbb">

          <h2 className="help-wrapper">
            {t('validation.receipts-signed')}
          </h2>
          {documents?.map((document, index) => (
            <div key={document.id + '-' + index} className='col-md-12 u-mb0'>
              <SignDocument folderId={dossierId} document={document} />
            </div>
          ))}
        </div>
        {downloadDocWidget}
        </>}
        <div className="flex-container u-mtl">
          <div className="col-md-12 u-ma0 u-pa0">
            <div className="custom-main-card">
              <h2 className="text-2xl font-semibold text-slate-900 u-mbm">{t('validation.receipts')}</h2>
              <div className="u-mbs u-mxs">
                {(receipts && Object.keys(receipts).length > 0) ? (
                  Object.keys(receipts).map((type) => (
                    <div key={type} className='u-mbb'>
                      <h5 className='text-lg font-medium pl-2 border-l-4 border-slate-300 font-400 u-mbs'>{type}</h5>
                      {receipts[type].map((receipt) => (
                        <Receipt
                          key={receipt.id}
                          receipt={receipt}
                          state={state}
                          onInvalidateToggle={onInvalidateToggle}
                          onCommentChange={onCommentChange}
                          invalidElement={invalidElements.find(element => element.receiptId === receipt.id) ?? null}
                        />
                      ))}
                    </div>
                  ))
                ) : (
                  <span className="u-txt-italic">{t('validation.no-receipt-found')}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiptsValidation
